import { PERIOD_IN_SECONDS, WETH9_ADDRESS } from '@bitly/sdk/constants'
import {
	ExchangeMarketType,
	ExchangeOrderDetails,
	MarketsVolumes,
	OrderbookType,
	OrderDirection,
	TokenInfoTypeWithAddress,
	ListTokenProps,
	PairedliquidityType,
	GetUserLiqInfoType,
	LiquidityBookType,
} from '@bitly/sdk/types'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { notifyError } from 'components/ErrorNotifier'
import { OrderType } from "types/common"
import { monitorTransaction } from 'contexts/RelayerContext'
import { fetchAllMarkets, fetchAllTokens } from 'state/manage/actions'
import { selectCurrentMarketPrice } from 'state/prices/selectors'
import { FetchStatus, ThunkConfig } from 'state/types'
import { fetchBalance } from 'state/wallet/actions'
import logError from 'utils/logError'
import { formatOrderId } from 'utils/string'
import { setMakeOrderStatus, setOrderErrorMessage, setOrderSize, setOrderTotal, setPlaceLiquidityStatus } from './reducer'
import {
	selectCurrentMarketAsset,
	selectCurrentMarketInfo,
	selectDesireMode,
	selectLiq2AmountRange,
	selectLiq2AmountRangeQueryStatus,
	selectOrderbookWidth,
	selectOrderDirection,
	selectOrderPrice,
	selectOrderSize,
	selectOrderTotal,
	selectOrderType,
	selectSlippage
} from './selectors'
import { ETH_TOKEN_INFO } from 'constants/address'
import { selectBalanceInBank } from 'state/wallet/selectors'

export const fetchMarkets = createAsyncThunk<
	{ markets: ExchangeMarketType[]; networkId: number } | undefined,
	void,
	ThunkConfig
>('exchange/fetchMarkets', async (_, { extra: { sdk } }) => {
	try {
		const markets = await sdk.exchange.getMarketsInfo([])
		const networkId = sdk.context.networkId
		return { markets, networkId }
	} catch (err) {
		logError(err)
		notifyError('Failed to fetch markets', err)
		throw err
	}
})

export const fetchTokenList = createAsyncThunk<
	{ tokens: TokenInfoTypeWithAddress[]; networkId: number } | undefined,
	void,
	ThunkConfig
>('exchange/fetchTokenList', async (_, { extra: { sdk } }) => {
	try {
		const networkId = sdk.context.networkId
		let tokens = await sdk.exchange.getTokensInfo([])
		if (tokens.find(e=>e.address === WETH9_ADDRESS[networkId])) {
			tokens = [...tokens, ETH_TOKEN_INFO]
		}
		return { tokens, networkId }
	} catch (err) {
		logError(err)
		notifyError('Failed to fetch markets', err)
		throw err
	}
})

export const fetchDailyVolumes = createAsyncThunk<
	{ volumes: MarketsVolumes; networkId: number } | undefined,
	void,
	ThunkConfig
>('exchange/fetchDailyVolumes', async (_, { extra: { sdk } }) => {
		try {
			const markets = await sdk.exchange.getMarketsInfo([])
			const volumesToday = await sdk.exchange.getVolumes(markets.map(e=>e.marketAddress))
			const volumesYesterday = await sdk.exchange.getVolumes(markets.map(e=>e.marketAddress), -PERIOD_IN_SECONDS.ONE_DAY)
			const volumes: MarketsVolumes = volumesToday
			for (const market in volumes) {
				if (Object.prototype.hasOwnProperty.call(volumes, market)) {
					volumes[market] -= volumesYesterday[market] ?? 0
				}
			}
			const networkId = sdk.context.networkId
			return { volumes, networkId }
		} catch (err) {
			logError(err)
			notifyError('Failed to fetch markets', err)
			throw err
		}
	}
)

export const fetchOpenOrders = createAsyncThunk<
	{ openOrders: ExchangeOrderDetails; networkId: number } | undefined,
	void,
	ThunkConfig
>('exchange/fetchOpenOrders', async (_, { extra: { sdk } }) => {
	try {
		const markets = sdk.exchange.getMarketsInfo([])
		const openOrders = await sdk.exchange.getLimitOrders(markets.map(e=>e.marketAddress))
		const networkId = sdk.context.networkId
		return { openOrders, networkId }
	} catch (err) {
		logError(err)
		notifyError('Failed to fetch open orders', err)
		throw err
	}
})

export const claimAllEarnings = createAsyncThunk<
	void,
	{ market: string },
	ThunkConfig
>('exchange/claimAllEarnings', async ({market}, { dispatch, extra: { sdk } }) => {
	monitorTransaction({
		transaction: () => sdk.exchange.claimAllEarnings(market),
		onTxConfirmed: () => {
			dispatch(fetchOpenOrders())
			dispatch(fetchBalance())
		},
		onTxFailed: () => {
		},
	})
})

export const claimEarning = createAsyncThunk<
	void,
	{ market: string, limOrderSlot: number },
	ThunkConfig
>('exchange/claimEarning', async ({market, limOrderSlot}, { dispatch, extra: { sdk } }) => {
	monitorTransaction({
		transaction: () => sdk.exchange.claimEarning(market, limOrderSlot),
		onTxConfirmed: () => {
			dispatch({ type: 'exchange/setClaimEarningStatus', payload: FetchStatus.Success })
			dispatch(fetchOpenOrders())
			dispatch(fetchBalance())
		},
		onTxFailed: () => {
			dispatch({ type: 'wallet/setClaimEarningStatus', payload: FetchStatus.Error })
		},
	})
})

export const cancelOrder = createAsyncThunk<
	void,
	{ market: string, limOrderSlot: number },
	ThunkConfig
>('exchange/cancelOrder', async ({market, limOrderSlot}, { dispatch, extra: { sdk } }) => {
	monitorTransaction({
		transaction: () => sdk.exchange.cancelLimitOrder(market, limOrderSlot),
		onTxConfirmed: () => {
			dispatch({ type: 'exchange/setCancelOrderStatus',
				payload: {id: formatOrderId(market, limOrderSlot), status: FetchStatus.Success}
			})
			dispatch(fetchOpenOrders())
			dispatch(fetchOrderbook())
			dispatch(fetchBalance())
		},
		onTxFailed: () => {
			dispatch({ type: 'exchange/setCancelOrderStatus',
				payload: {id: formatOrderId(market, limOrderSlot), status: FetchStatus.Error}
			})
		},
	})
})

export const listToken = createAsyncThunk<
	void,
	ListTokenProps,
	ThunkConfig
>('exchange/listToken', async (info, { dispatch, extra: { sdk } }) => {
	monitorTransaction({
		transaction: () => sdk.manage.listToken(info),
		onTxConfirmed: () => {
			dispatch({ type: 'exchange/setListTokenStatus',
				payload: FetchStatus.Success
			})
			dispatch(fetchAllTokens())
		},
		onTxFailed: () => {
			dispatch({ type: 'exchange/setListTokenStatus',
				payload: FetchStatus.Error
			})
		},
	})
})

export const listPair = createAsyncThunk<
	void,
	{ base: string, quote: string, feeTier: number, initialPrice: number },
	ThunkConfig
>('exchange/listPair', async ({ base, quote, feeTier, initialPrice }, { dispatch, extra: { sdk } }) => {
	monitorTransaction({
		transaction: () => sdk.manage.listPair(base, quote, feeTier, initialPrice),
		onTxConfirmed: () => {
			dispatch({ type: 'exchange/setListPairStatus',
				payload: FetchStatus.Success
			})
			dispatch(fetchAllMarkets())
		},
		onTxFailed: () => {
			dispatch({ type: 'exchange/setListPairStatus',
				payload: FetchStatus.Error
			})
		},
	})
})

export const placeOrder = createAsyncThunk<
	void,
	void,
	ThunkConfig
>('exchange/placeOrder', async (_, { getState, dispatch, extra: { sdk } }) => {
	const direction = selectOrderDirection(getState())
	const amount = Number(selectOrderSize(getState()))
	const total = Number(selectOrderTotal(getState()))
	const price = Number(selectOrderPrice(getState()))
	const type = selectOrderType(getState())
	const slippage = selectSlippage(getState())
	const market = selectCurrentMarketAsset(getState())
	const curPrice = selectCurrentMarketPrice(getState())

	const makeOrder = async () => {
		dispatch(setMakeOrderStatus(FetchStatus.Loading))
		const volume = direction === OrderDirection.buy ? total : amount
		if (type === 'limit') {
			return await sdk.exchange.placeLimitOrder(market, direction, price, volume)
		} else {
			return await sdk.exchange.placeMarketOrder(market, direction, volume, curPrice, Math.floor(slippage*10000))
		}
	}

	monitorTransaction({
		transaction: makeOrder,
		onTxSent: () => {
			dispatch(setMakeOrderStatus(FetchStatus.Success))
		},
		onTxFailed: () => {
			dispatch(setMakeOrderStatus(FetchStatus.Error))
			dispatch(fetchOpenOrders())
			dispatch(fetchDailyVolumes())
			dispatch(fetchOrderbook())
			dispatch(fetchBalance())
		},
		onTxConfirmed: () => {
			dispatch(fetchOpenOrders())
			dispatch(fetchDailyVolumes())
			dispatch(fetchOrderbook())
			dispatch(fetchBalance())
			dispatch(setOrderSize('0.00'))
			dispatch(setOrderTotal('0.00'))
		},
	})
})

export const fetchOrderbook = createAsyncThunk<
	{ orderbook: OrderbookType; market: string } | undefined,
	void,
	ThunkConfig
>('exchange/fetchOrderbook', async (_, { getState, extra: { sdk } }) => {
	const market = selectCurrentMarketAsset(getState())
	const width = selectOrderbookWidth(getState())
	const curPrice = selectCurrentMarketPrice(getState())

	const high = curPrice * (1 + width)
	const low = curPrice * (1 - width)

	try {
		const orderbook = await sdk.exchange.getOrderbook(market, {high, low})
		return { orderbook, market }
	} catch (err) {
		logError(err)
		notifyError('Failed to fetch orderbook', err)
		throw err
	}
})

export const liq2AmountRange = createAsyncThunk<
	{ amountRange: PairedliquidityType; market: string } | undefined,
	{ lowPrice: number; highPrice: number; liquidityScale: number },
	ThunkConfig
>('exchange/liq2AmountRange', async ({ lowPrice, highPrice, liquidityScale }, { getState, extra: { sdk } }) => {
	const market = selectCurrentMarketAsset(getState())

	try {
		const amountRange = await sdk.liquidity.liq2AmountRange({
			marketAddress: market,
			lowPrice,
			highPrice,
			liquidityScale,
		})
		return { amountRange, market }
	} catch (err) {
		logError(err)
		notifyError('Failed to fetch liquidity amount range', err)
		throw err
	}
})

export const placeLiquidity = createAsyncThunk<
	void,
	{ minPrice: number; maxPrice: number },
	ThunkConfig
>('exchange/placeLiquidity', async ({ minPrice, maxPrice }, { getState, dispatch, extra: { sdk } }) => {
	const market = selectCurrentMarketAsset(getState())
	const liq2Range = selectLiq2AmountRange(getState())
	const liq2RangeStatus = selectLiq2AmountRangeQueryStatus(getState())

	if (liq2RangeStatus.status !== FetchStatus.Success || !liq2Range) {
		notifyError('Invalid liquidity range')
		return
	}

	const placeLiquidity = async () => {
		dispatch(setPlaceLiquidityStatus(FetchStatus.Loading))
		return await sdk.liquidity.mint({
			market,
			priceLeft: minPrice,
			priceRight: maxPrice,
			amountX: liq2Range.amountX,
			amountY: liq2Range.amountY,
			minAmountX: 0,
			minAmountY: 0,
		})
	}

	monitorTransaction({
		transaction: placeLiquidity,
		onTxSent: () => {
			dispatch(setPlaceLiquidityStatus(FetchStatus.Success))
		},
		onTxFailed: () => {
			dispatch(setPlaceLiquidityStatus(FetchStatus.Error))
		},
		onTxConfirmed: () => {
			dispatch(fetchBalance())
		},
	})
})

export const fetchUserLiquidities = createAsyncThunk<
	{ userLiquidities: GetUserLiqInfoType[]; market: string } | undefined,
	void,
	ThunkConfig
>('exchange/fetchUserLiquidities', async (_, { getState, extra: { sdk } }) => {
	const market = selectCurrentMarketAsset(getState())

	try {
		const userLiquidities = await sdk.liquidity.getUserLiqInfo(market)
		return { userLiquidities, market }
	} catch (err) {
		logError(err)
		notifyError('Failed to fetch user liquidities', err)
		throw err
	}
})

export const collectLiquidity = createAsyncThunk<
	void,
	{ market: string, lid: number },
	ThunkConfig
>('exchange/collectLiquidity', async ({ market, lid }, { dispatch, extra: { sdk } }) => {
	monitorTransaction({
		transaction: () => sdk.liquidity.collect(market, lid),
		onTxConfirmed: () => {
			dispatch({ type: 'exchange/setCollectLiquidityStatus', payload: { id: `${market}-${lid}`, status: FetchStatus.Success } })
			dispatch(fetchUserLiquidities())
			dispatch(fetchBalance())
		},
		onTxFailed: () => {
			dispatch({ type: 'exchange/setCollectLiquidityStatus', payload: { id: `${market}-${lid}`, status: FetchStatus.Error } })
		},
	})
})

export const burnLiquidity = createAsyncThunk<
	void,
	{ market: string, lid: number },
	ThunkConfig
>('exchange/burnLiquidity', async ({ market, lid }, { dispatch, extra: { sdk } }) => {
	monitorTransaction({
		transaction: () => sdk.liquidity.burn(market, lid),
		onTxConfirmed: () => {
			dispatch({ type: 'exchange/setBurnLiquidityStatus', payload: { id: `${market}-${lid}`, status: FetchStatus.Success } })
			dispatch(fetchUserLiquidities())
			dispatch(fetchBalance())
		},
		onTxFailed: () => {
			dispatch({ type: 'exchange/setBurnLiquidityStatus', payload: { id: `${market}-${lid}`, status: FetchStatus.Error } })
		},
	})
})

export const fetchLiquidityBook = createAsyncThunk<
	{ liquidityBook: LiquidityBookType; market: string } | undefined,
	void,
	ThunkConfig
>('exchange/fetchLiquidityBook', async (_, { getState, extra: { sdk } }) => {
	const market = selectCurrentMarketAsset(getState())
	const width = selectOrderbookWidth(getState())
	const curPrice = selectCurrentMarketPrice(getState())

	const high = curPrice * (1 + width)
	const low = curPrice * (1 - width)

	try {
		const liquidityBook = await sdk.liquidity.getLiquiditybook(market, {high, low})
		return { liquidityBook, market }
	} catch (err) {
		logError(err)
		notifyError('Failed to fetch liquidity book', err)
		throw err
	}
})

export const fetchTargetSwapAmount = createAsyncThunk<
	void,
	void,
	ThunkConfig
>('exchange/fetchTargetSwapAmount', async (_, { getState, dispatch, extra: { sdk } }) => {
	const marketInfo = selectCurrentMarketInfo(getState())
	const curPrice = selectCurrentMarketPrice(getState())
	const direction = selectOrderDirection(getState())
	const slippage = selectSlippage(getState())
	const desireMode = selectDesireMode(getState())
	const orderSize = selectOrderSize(getState())
	const orderTotal = selectOrderTotal(getState())
	const orderType = selectOrderType(getState())
	const orderPrice = selectOrderPrice(getState())
	const balances = selectBalanceInBank(getState())

	dispatch(setOrderErrorMessage(undefined))

	if (!marketInfo || !curPrice || Number(curPrice) <= 0) {
		return
	}

	// For limit orders

	if (orderType === 'limit') {
		if (Number(orderTotal) <= 0 && Number(orderSize) <= 0) {
			return
		}
		if (orderPrice && Number(orderPrice) <= 0) {
			return
		}

		// For limit orders, calculate volume needed using curPrice
		let volumeNeeded: number = 0
		if (!desireMode) {
			if (direction === OrderDirection.buy) {
				// Buying: need quote token balance = orderTotal
				volumeNeeded = Number(orderTotal)
			} else {
				// Selling: need base token balance = orderSize
				volumeNeeded = Number(orderSize)
			}
		} else {
			if (direction === OrderDirection.buy) {
				// Buying in desireMode: need base token balance = orderSize * orderPrice
				volumeNeeded = Number(orderSize) * Number(orderPrice)
			} else {
				// Selling in desireMode: need quote token balance = orderTotal / orderPrice
				volumeNeeded = Number(orderTotal) / Number(orderPrice)
			}
		}

		// Check balance sufficiency
		const requiredToken = direction === OrderDirection.buy
			? marketInfo.tokenY.address
			: marketInfo.tokenX.address

		if (volumeNeeded > 0 && volumeNeeded > balances[requiredToken]) {
			dispatch(setOrderErrorMessage('Insufficient balance'))
			return
		}

		// For limit orders, calculate and fill the missing field using orderPrice
		dispatch(setOrderErrorMessage(undefined))
		if (!desireMode) {
			if (direction === OrderDirection.buy) {
				// User inputs orderTotal, calculate orderSize = orderTotal / orderPrice
				if (Number(orderTotal) > 0 && Number(orderPrice) > 0) {
					dispatch(setOrderSize((Number(orderTotal) / Number(orderPrice)).toString()))
				}
			} else {
				// User inputs orderSize, calculate orderTotal = orderSize * orderPrice
				if (Number(orderSize) > 0 && Number(orderPrice) > 0) {
					dispatch(setOrderTotal((Number(orderSize) * Number(orderPrice)).toString()))
				}
			}
		} else {
			if (direction === OrderDirection.buy) {
				// User inputs orderSize, calculate orderTotal = orderSize * orderPrice
				if (Number(orderSize) > 0 && Number(orderPrice) > 0) {
					dispatch(setOrderTotal((Number(orderSize) * Number(orderPrice)).toString()))
				}
			} else {
				// User inputs orderTotal, calculate orderSize = orderTotal / orderPrice
				if (Number(orderTotal) > 0 && Number(orderPrice) > 0) {
					dispatch(setOrderSize((Number(orderTotal) / Number(orderPrice)).toString()))
				}
			}
		}
		return
	}

	// For market orders

	let volume: number = 0

	if (!desireMode) {
		if (direction === OrderDirection.buy) {
			volume = Number(orderTotal)
		} else {
			volume = Number(orderSize)
		}
	} else {
		if (direction === OrderDirection.buy) {
			volume = Number(orderSize)
		} else {
			volume = Number(orderTotal)
		}
	}

	console.log('ww: fetchTargetSwapAmount: volume', volume)

	if (volume <= 0) {
		return
	}

	try {
		const { amountOut } = await sdk.exchange.getTargetSwapAmount(
			marketInfo.marketAddress,
			direction,
			volume,
			Number(curPrice),
			Math.floor(slippage*10000),
			desireMode
		)

		console.log('ww: fetchTargetSwapAmount: amountOut', amountOut)
		if (!desireMode) {
			if (direction === OrderDirection.buy) {
				dispatch(setOrderSize(amountOut.toString()))
			} else {
				dispatch(setOrderTotal(amountOut.toString()))
			}
		} else {
			if (direction === OrderDirection.buy) {
				dispatch(setOrderTotal(amountOut.toString()))
			} else {
				dispatch(setOrderSize(amountOut.toString()))
			}
		}

	} catch (err) {
		logError(err)
		let errorMessage = err instanceof Error ? err.message : 'Calculate swap ratio failed'
		// Extract errorArgs from error message, e.g., errorArgs=["Out of Slippage"]
		const match = errorMessage.match(/errorArgs=\[?"([^"]+)"\]?/)
		if (match) {
			errorMessage = match[1]
			switch (errorMessage) {
				case 'Out of Slippage':
					errorMessage = 'Out of Slippage'
					break
				case 'INSUF_BALANCE_TRANS':
					errorMessage = 'Insufficient balance'
			}
		}
		dispatch(setOrderErrorMessage(errorMessage))
	}
})